import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '../../components/ui/Badge';
import { Select } from '../../components/ui/Input';
import { PaginaCrud, type CampoFormulario } from '../../components/layout/PaginaCrud';
import type { FilaInventario } from '../../../data/repositories/RepositorioInventario';
import { repositorioCatalogos } from '../../../data/repositories/RepositorioCatalogos';
import { repositorioTipos } from '../../../data/repositories/RepositorioTipos';
import { ConfigurarActuador } from './ConfigurarActuador';
import { Button } from '../../components/ui/Button';
import { aNumero, aObjeto, aTexto } from '../../../data/mappers/conversiones';
import estilos from '../../components/layout/PaginaCrud.module.css';

interface ActuadorFila {
  id: number;
  nombre: string;
  tipo: string;
  tipoId: number | null;
  clienteId: number | null;
  /** Ajustes guardados por encima de los de fábrica de su tipo. */
  config: Record<string, unknown>;
  codigoEnvio: string | null;
  nodoNombre: string;
  areaNombre: string;
  clienteNombre: string;
}

export function Actuadores() {
  const [clienteId, setClienteId] = useState<number | null>(null);
  const [areaId, setAreaId] = useState<number | null>(null);
  const [nodoFiltroId, setNodoFiltroId] = useState<number | null>(null);
  const [configurando, setConfigurando] = useState<ActuadorFila | null>(null);

  const clientes = useQuery({
    queryKey: ['catalogo', 'clientes'],
    queryFn: () => repositorioCatalogos.clientes(),
    staleTime: 5 * 60_000,
  });

  const areasFiltro = useQuery({
    queryKey: ['catalogo', 'areas', clienteId],
    queryFn: () => repositorioCatalogos.areasDeCliente(clienteId),
    enabled: clienteId !== null,
    staleTime: 5 * 60_000,
  });

  const nodosFiltro = useQuery({
    queryKey: ['catalogo', 'nodos', areaId],
    queryFn: () => repositorioCatalogos.nodosDeArea(areaId),
    enabled: areaId !== null,
    staleTime: 5 * 60_000,
  });

  /*
   * Listas completas para la cascada del formulario. Las de la barra de
   * filtros no sirven: vienen ya acotadas por lo que se esté mirando, y al
   * crear un actuador hay que poder elegir cualquier cliente.
   */
  const areasFormulario = useQuery({
    queryKey: ['catalogo', 'areas', 'con-cliente'],
    queryFn: () => repositorioCatalogos.areasConCliente(),
    staleTime: 5 * 60_000,
  });

  const nodos = useQuery({
    queryKey: ['catalogo', 'nodos', 'con-area'],
    queryFn: () => repositorioCatalogos.nodosConArea(),
    staleTime: 5 * 60_000,
  });

  const tipos = useQuery({
    queryKey: ['tipos', 'actuadores'],
    queryFn: () => repositorioTipos.tiposDeActuadorPorCliente(),
    staleTime: 5 * 60_000,
  });

  const mapear = useCallback(
    (fila: FilaInventario): ActuadorFila => ({
      id: aNumero(fila.id) ?? aNumero(fila.actuador_id) ?? 0,
      nombre: aTexto(fila.nombre) ?? aTexto(fila.nombre_actuador) ?? 'Sin nombre',
      tipo: aTexto(fila.tipo_nombre) ?? aTexto(fila.tipo_codigo) ?? '—',
      tipoId: aNumero(fila.tipo_id),
      clienteId: aNumero(fila.cliente_id),
      config: aObjeto(fila.config ?? fila.capabilities),
      codigoEnvio: aTexto(fila.codigo_envio),
      nodoNombre: aTexto(fila.nodo_nombre) ?? '—',
      areaNombre: aTexto(fila.area_nombre) ?? '—',
      clienteNombre: aTexto(fila.cliente_nombre) ?? '—',
    }),
    [],
  );

  /** Tipos habilitados, sin repetir: la vista los trae una vez por cliente. */
  const tiposDisponibles = useCallback(
    (clienteElegido: unknown) => {
      const vistos = new Map<number, string>();

      for (const t of tipos.data ?? []) {
        if (!t.habilitado) continue;
        // Sin cliente elegido se ofrecen todos; con cliente, sólo los suyos.
        if (clienteElegido && String(t.clienteId ?? '') !== String(clienteElegido)) continue;
        if (!vistos.has(t.id)) vistos.set(t.id, t.nombre);
      }

      return [...vistos].map(([id, nombre]) => ({ valor: id, etiqueta: nombre }));
    },
    [tipos.data],
  );

  const campos: CampoFormulario[] = useMemo(
    () => [
      /*
       * Cliente y área no se guardan —el actuador cuelga del nodo—, pero sin
       * ellos el desplegable de nodos son todos los nodos de todos los
       * clientes. Acotan, igual que en la aplicación anterior.
       */
      {
        clave: 'cliente_id',
        etiqueta: 'Cliente',
        tipo: 'select',
        requerido: true,
        placeholder: 'Elige un cliente',
        limpiaAlCambiar: ['area_id', 'nodo_id', 'tipo_id'],
        opciones: (clientes.data ?? []).map((c) => ({ valor: c.id, etiqueta: c.nombre })),
      },
      {
        clave: 'area_id',
        etiqueta: 'Área',
        tipo: 'select',
        requerido: true,
        placeholder: 'Elige un área',
        limpiaAlCambiar: ['nodo_id'],
        opciones: (valores) =>
          (areasFormulario.data ?? [])
            .filter((a) => String(a.clienteId ?? '') === String(valores.cliente_id ?? ''))
            .map((a) => ({ valor: a.id, etiqueta: a.nombre })),
      },
      {
        clave: 'nodo_id',
        etiqueta: 'Nodo',
        tipo: 'select',
        requerido: true,
        placeholder: 'Elige un nodo',
        ayuda: 'Equipo al que está conectado. Se acota al área elegida',
        opciones: (valores) =>
          (nodos.data ?? [])
            .filter((n) => String(n.areaId ?? '') === String(valores.area_id ?? ''))
            .map((n) => ({ valor: n.id, etiqueta: n.nombre })),
      },
      {
        clave: 'nombre',
        etiqueta: 'Nombre',
        tipo: 'texto',
        requerido: true,
        placeholder: 'Bomba principal, Portón, Sirena…',
      },
      {
        clave: 'tipo_id',
        etiqueta: 'Tipo de actuador',
        tipo: 'select',
        requerido: true,
        placeholder: 'Elige un tipo',
        ayuda: 'Determina qué órdenes admite el equipo. Sólo los habilitados al cliente',
        opciones: (valores) => tiposDisponibles(valores.cliente_id),
      },
      {
        clave: 'codigo_envio',
        etiqueta: 'Código de envío',
        tipo: 'texto',
        ayuda: 'Identificador con el que el nodo recibe las órdenes de este equipo',
      },
    ],
    [clientes.data, areasFormulario.data, tiposDisponibles, nodos.data],
  );

  return (
    <>
      <PaginaCrud<ActuadorFila>
        entidad="actuadores"
        titulo="Actuadores"
        descripcion="Equipos que se pueden accionar a distancia."
        textoVacio="Da de alta un actuador para poder accionarlo desde la consola."
        mapear={mapear}
        claveFila={(a) => a.id}
        textoBusqueda={(a) =>
          `${a.nombre} ${a.tipo} ${a.codigoEnvio ?? ''} ${a.nodoNombre} ${a.areaNombre} ${a.clienteNombre}`
        }
        idDeFila={(fila) => aNumero(fila.id) ?? aNumero(fila.actuador_id) ?? 0}
        campos={campos}
        filtrosConsulta={{ cliente_id: clienteId, area_id: areaId, nodo_id: nodoFiltroId }}
        filtrosExtra={
          <>
            <Select
              etiqueta="Cliente"
              placeholder="Todos los clientes"
              value={clienteId ?? ''}
              onChange={(e) => {
                setClienteId(e.target.value ? Number(e.target.value) : null);
                setAreaId(null);
                setNodoFiltroId(null);
              }}
              opciones={(clientes.data ?? []).map((c) => ({ valor: c.id, etiqueta: c.nombre }))}
            />
            <Select
              etiqueta="Área"
              placeholder={clienteId ? 'Todas las áreas' : 'Elige un cliente'}
              disabled={!clienteId}
              value={areaId ?? ''}
              onChange={(e) => {
                setAreaId(e.target.value ? Number(e.target.value) : null);
                setNodoFiltroId(null);
              }}
              opciones={(areasFiltro.data ?? []).map((a) => ({ valor: a.id, etiqueta: a.nombre }))}
            />
            <Select
              etiqueta="Nodo"
              placeholder={areaId ? 'Todos los nodos' : 'Elige un área'}
              disabled={!areaId}
              value={nodoFiltroId ?? ''}
              onChange={(e) => setNodoFiltroId(e.target.value ? Number(e.target.value) : null)}
              opciones={(nodosFiltro.data ?? []).map((n) => ({ valor: n.id, etiqueta: n.nombre }))}
            />
          </>
        }
        aFormulario={(fila) => ({
          // Cliente y área abren la cascada por donde está el actuador.
          cliente_id: aNumero(fila.cliente_id) ?? '',
          area_id: aNumero(fila.area_id) ?? '',
          nombre: aTexto(fila.nombre) ?? '',
          tipo_id: aNumero(fila.tipo_id) ?? '',
          nodo_id: aNumero(fila.nodo_id) ?? '',
          codigo_envio: aTexto(fila.codigo_envio) ?? '',
        })}
        // Cliente y área se quedan fuera: la tabla no tiene esas columnas.
        aRegistro={(v) => ({
          nombre: String(v.nombre ?? '').trim(),
          tipo_id: Number(v.tipo_id),
          nodo_id: Number(v.nodo_id),
          codigo_envio: String(v.codigo_envio ?? '').trim() || null,
        })}
        textoBorrado={(a) =>
          `¿Eliminar el actuador «${a.nombre}»?\n\n` +
          'Se borrarán también las acciones automáticas y las reglas horarias que lo accionaban.'
        }
        columnas={[
          /*
           * El identificador se muestra sólo aquí. Hace falta para configurar
           * el nodo, que es donde se transcribe a mano; en las demás pantallas
           * sería ruido. Un clic lo selecciona entero para copiarlo sin
           * arrastrar el ratón.
           */
          {
            clave: 'id',
            encabezado: 'ID',
            render: (a) => (
              <span className={estilos.identificador} title="Identificador para configurar el nodo">
                {a.id}
              </span>
            ),
          },
          {
            clave: 'nombre',
            encabezado: 'Actuador',
            movil: 'titulo',
            render: (a) => <span className={estilos.nombre}>{a.nombre}</span>,
          },
          {
            clave: 'ubicacion',
            encabezado: 'Ubicación',
            movil: 'subtitulo',
            render: (a) => (
              <span className={estilos.secundario}>
                {a.clienteNombre} · {a.areaNombre} · {a.nodoNombre}
              </span>
            ),
          },
          {
            clave: 'tipo',
            encabezado: 'Tipo',
            movil: 'detalle',
            render: (a) => <Badge color="var(--text-2)">{a.tipo}</Badge>,
          },
          {
            clave: 'codigo',
            encabezado: 'Código de envío',
            movil: 'detalle',
            render: (a) => <span className={estilos.secundario}>{a.codigoEnvio ?? '—'}</span>,
          },
        ]}
        accionesExtra={(a) => (
          <Button
            tamano="sm"
            variante="secundario"
            icono="ajustes"
            title="Configurar el equipo"
            motivoDeshabilitado={
              a.tipoId === null || a.clienteId === null
                ? 'No se pudo identificar el tipo de este actuador'
                : null
            }
            onClick={() => setConfigurando(a)}
          />
        )}
      />

      <ConfigurarActuador
        abierto={configurando !== null}
        actuadorId={configurando?.id ?? null}
        actuadorNombre={configurando?.nombre ?? ''}
        tipoId={configurando?.tipoId ?? null}
        clienteId={configurando?.clienteId ?? null}
        configActual={configurando?.config ?? {}}
        onCerrar={() => setConfigurando(null)}
        onGuardado={() => setConfigurando(null)}
      />
    </>
  );
}
